import React, { useState, useEffect } from 'react';
import useSWR from 'swr';
import ServiceToggle from './ServiceToggle';
import { 
  CheckCircle, Clock, Star, ShoppingCart, UserPlus, AlertTriangle,
  FileText, Bell, Loader2, BarChart3, MessageSquare, Phone, Calendar,
  Zap, Users, Mail
} from 'lucide-react';
import { toast } from 'sonner';
import apiV2, { orgApi } from '../utils/apiV2';
import TabPageLayout from './TabPageLayout';

// SWR fetcher
const fetcher = async (url) => {
  const response = await apiV2.get(url);
  return response.data;
};

/**
 * NotificationsTab - Shows email and SMS notification toggles
 * Auto-saves on toggle, preset times
 */
const NotificationsTab = () => {
  const [savingKey, setSavingKey] = useState(null);
  
  // Settings state - includes SMS toggles for high-impact notifications
  const [settings, setSettings] = useState({
    // Customer - Email
    reminderEnabled: true,        // 24h reminder before trip
    thankYouEnabled: true,        // Thank you after trip
    abandonedCartEnabled: true,   // Cart recovery
    // Customer - SMS (high-impact only)
    confirmationSmsEnabled: true, // Booking confirmation via SMS
    reminderSmsEnabled: false,    // 24h reminder via SMS
    cancellationSmsEnabled: false, // Cancellation alerts via SMS
    // Operator
    newBookingEnabled: true,      // New booking alert email
    newBookingSmsEnabled: true,   // New booking alert SMS (default ON)
    newLeadEnabled: true,         // New lead alert
    weeklyReportEnabled: true,    // Weekly performance report
    // Operator digest
    upcomingBookingsEnabled: true,    // Tomorrow's bookings digest email
    upcomingBookingsSmsEnabled: false, // Tomorrow's bookings digest SMS
  });

  // AoC state — separate from notification settings
  const [aocEnabled, setAocEnabled] = useState(false);
  const [aocSaving, setAocSaving] = useState(false);

  // Fetch org data for notification settings
  const { data: orgData, isLoading, mutate } = useSWR(
    '/auth/organizations/me',
    fetcher,
    { revalidateOnFocus: true, dedupingInterval: 30000 }
  );

  const orgId = orgData?.id || orgData?.organizationId;

  // Fetch AoC settings — note: apiV2 base already includes /api/v2
  const { data: aocData } = useSWR(
    orgId ? `/organizations/${orgId}/aoc/settings` : null,
    fetcher,
    { dedupingInterval: 60000 }
  );

  useEffect(() => {
    if (aocData) setAocEnabled(aocData.aocEnabled || false);
  }, [aocData]);

  const handleAocToggle = async (value) => {
    setAocEnabled(value);
    setAocSaving(true);
    try {
      await apiV2.patch(`/organizations/${orgId}/aoc/settings`, { aocEnabled: value });
      toast.success(value ? 'AoC enabled — team will be texted on new bookings' : 'AoC disabled');
    } catch {
      setAocEnabled(!value);
      toast.error('Failed to update AoC setting');
    } finally {
      setAocSaving(false);
    }
  };

  // Load settings from org data
  useEffect(() => {
    // Try to get notification settings from multiple possible locations
    const ns = orgData?.settings?.notificationSettings || orgData?.notificationSettings || {};
    if (Object.keys(ns).length > 0 || orgData) {
      setSettings({
        reminderEnabled: ns.reminderEmailEnabled ?? true,
        thankYouEnabled: ns.thankYouEmailEnabled ?? true,
        abandonedCartEnabled: ns.abandonedCartEmailEnabled ?? true,
        confirmationSmsEnabled: ns.confirmationSmsEnabled ?? true,
        reminderSmsEnabled: ns.reminderSmsEnabled ?? false,
        cancellationSmsEnabled: ns.cancellationSmsEnabled ?? false,
        newBookingEnabled: ns.newBookingEmailEnabled ?? true,
        newBookingSmsEnabled: ns.newBookingSmsEnabled ?? true,
        newLeadEnabled: ns.newLeadEmailEnabled ?? true,
        weeklyReportEnabled: ns.weeklyReportEmailEnabled ?? true,
        upcomingBookingsEnabled: ns.upcomingBookingsEmailEnabled ?? true,
        upcomingBookingsSmsEnabled: ns.upcomingBookingsSmsEnabled ?? false,
      });
    }
  }, [orgData]);

  // Auto-save when toggle changes
  const handleToggle = async (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setSavingKey(key);
    
    try {
      const updatedSettings = { ...settings, [key]: value };
      
      // Build the notification settings object
      const notificationSettings = {
        // Customer notifications - Email
        confirmationEmailEnabled: true,
        reminderEmailEnabled: updatedSettings.reminderEnabled,
        reminderHoursBefore: 24,
        thankYouEmailEnabled: updatedSettings.thankYouEnabled,
        thankYouDelayHours: 24,
        thankYouIncludeReview: true,
        abandonedCartEmailEnabled: updatedSettings.abandonedCartEnabled,
        abandonedCartDelayMinutes: 30,
        // Customer notifications - SMS (high-impact only)
        confirmationSmsEnabled: updatedSettings.confirmationSmsEnabled,
        reminderSmsEnabled: updatedSettings.reminderSmsEnabled,
        cancellationSmsEnabled: updatedSettings.cancellationSmsEnabled,
        // Operator notifications
        newBookingEmailEnabled: updatedSettings.newBookingEnabled,
        newBookingSmsEnabled: updatedSettings.newBookingSmsEnabled,
        newLeadEmailEnabled: updatedSettings.newLeadEnabled,
        newLeadSmsEnabled: false,
        newLeadDelayMinutes: 10,
        weeklyReportEmailEnabled: updatedSettings.weeklyReportEnabled,
        // Operator upcoming bookings digest
        upcomingBookingsEmailEnabled: updatedSettings.upcomingBookingsEnabled,
        upcomingBookingsSmsEnabled: updatedSettings.upcomingBookingsSmsEnabled,
        // Keep other settings at their defaults
        cancellationEmailEnabled: true,
        reviewRequestEmailEnabled: false,
        reviewRequestSmsEnabled: false,
      };
      
      await apiV2.patch('/auth/organizations/me', {
        // Send under settings.notificationSettings for proper backend storage
        settings: {
          notificationSettings: notificationSettings
        },
        // Also send top-level fields for backwards compatibility
        thankYouEmailEnabled: updatedSettings.thankYouEnabled,
        thankYouEmailDelayHours: 24,
        thankYouEmailIncludeReview: true,
      });

      mutate();
      
      // Show success toast for SMS toggles
      if (key === 'confirmationSmsEnabled' || key === 'reminderSmsEnabled' || key === 'cancellationSmsEnabled') {
        toast.success(value ? 'SMS notifications enabled' : 'SMS notifications disabled');
      }
    } catch (error) {
      setSettings(prev => ({ ...prev, [key]: !value }));
      console.error('Error saving:', error);
      toast.error('Failed to save');
    } finally {
      setSavingKey(null);
    }
  };

  // Email-only notification row
  const NotificationRow = ({ icon: Icon, title, subtitle, settingKey, required = false }) => {
    const isEnabled = settings[settingKey];
    const isSaving = savingKey === settingKey;
    return (
      <div className="py-3.5 border-b border-gray-50 last:border-0">
        <div className="flex items-start gap-3">
          <div className="p-1.5 rounded-lg bg-gray-50 flex-shrink-0 mt-0.5">
            <Icon className="w-4 h-4 text-gray-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <span className="font-medium text-gray-900 text-sm">{title}</span>
                {required && (
                  <span className="ml-2 text-[10px] font-semibold bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded uppercase tracking-wide">Required</span>
                )}
                {subtitle && <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{subtitle}</p>}
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0 pt-0.5">
                <Mail className="w-3.5 h-3.5 text-gray-300" />
                {isSaving && <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-400" />}
                {required
                  ? <ServiceToggle checked={true} onChange={() => {}} disabled />
                  : <ServiceToggle checked={isEnabled} onChange={(v) => handleToggle(settingKey, v)} />
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Email + SMS notification row
  const NotificationRowWithSms = ({ icon: Icon, title, subtitle, emailKey, smsKey, emailRequired = false }) => {
    const emailEnabled = settings[emailKey];
    const smsEnabled = settings[smsKey];
    const isSavingEmail = savingKey === emailKey;
    const isSavingSms = savingKey === smsKey;
    return (
      <div className="py-3.5 border-b border-gray-50 last:border-0">
        <div className="flex items-start gap-3">
          <div className="p-1.5 rounded-lg bg-gray-50 flex-shrink-0 mt-0.5">
            <Icon className="w-4 h-4 text-gray-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <span className="font-medium text-gray-900 text-sm">{title}</span>
                {subtitle && <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{subtitle}</p>}
              </div>
              {/* Toggles — stacked on mobile, inline on desktop */}
              <div className="flex items-center gap-4 flex-shrink-0 pt-0.5">
                {/* Email */}
                <div className="flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5 text-gray-300" />
                  {isSavingEmail && <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-400" />}
                  {emailRequired
                    ? <ServiceToggle checked={true} onChange={() => {}} disabled />
                    : <ServiceToggle checked={emailEnabled} onChange={(v) => handleToggle(emailKey, v)} />
                  }
                </div>
                {/* SMS */}
                <div className="flex items-center gap-1.5">
                  <Phone className="w-3.5 h-3.5 text-gray-300" />
                  {isSavingSms && <Loader2 className="w-3.5 h-3.5 animate-spin text-green-400" />}
                  <ServiceToggle checked={smsEnabled} onChange={(v) => handleToggle(smsKey, v)} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <TabPageLayout
      title="Notifications"
      subtitle="Control what emails and texts are sent"
      loading={isLoading}
    >
      <div className="space-y-6">
        
        {/* Customer Emails */}
        <div className="bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.04)] p-4 sm:p-6">
          <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-blue-600" />
              <h3 className="font-semibold text-gray-900 text-sm">Customer Notifications</h3>
            </div>
            {/* Legend */}
            <div className="flex items-center gap-3 text-xs text-gray-400">
              <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> Email</span>
              <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> SMS</span>
            </div>
          </div>
          
          <NotificationRowWithSms
            icon={CheckCircle}
            title="Booking Confirmation"
            subtitle="Sent immediately when booked"
            emailKey="confirmationEnabled"
            smsKey="confirmationSmsEnabled"
            emailRequired={true}
          />
          
          <NotificationRowWithSms
            icon={AlertTriangle}
            title="Cancellation Alert"
            subtitle="When booking is cancelled"
            emailKey="cancellationEnabled"
            smsKey="cancellationSmsEnabled"
            emailRequired={true}
          />
          
          <NotificationRow
            icon={FileText}
            title="Waiver Request"
            subtitle="Included with confirmation"
            settingKey="waiverEnabled"
            required={true}
          />
          
          <NotificationRowWithSms
            icon={Clock}
            title="Trip Reminder"
            subtitle="24 hours before"
            emailKey="reminderEnabled"
            smsKey="reminderSmsEnabled"
          />
          
          <NotificationRow
            icon={Star}
            title="Thank You"
            subtitle="24 hours after trip"
            settingKey="thankYouEnabled"
          />
          
          <NotificationRow
            icon={ShoppingCart}
            title="Abandoned Cart"
            subtitle="30 min after leaving checkout"
            settingKey="abandonedCartEnabled"
          />
        </div>

        {/* Your Alerts */}
        <div className="bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.04)] p-4 sm:p-6">
          <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-green-600" />
              <h3 className="font-semibold text-gray-900 text-sm">Your Alerts</h3>
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-400">
              <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> Email</span>
              <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> SMS</span>
            </div>
          </div>
          
          <NotificationRowWithSms
            icon={CheckCircle}
            title="New Booking"
            subtitle="Instant alert when someone books"
            emailKey="newBookingEnabled"
            smsKey="newBookingSmsEnabled"
          />
          
          <NotificationRow
            icon={UserPlus}
            title="New Lead"
            subtitle="10 min after someone signs up and doesn't checkout"
            settingKey="newLeadEnabled"
          />
          
          <NotificationRow
            icon={BarChart3}
            title="Weekly Report"
            subtitle="Performance summary every Monday"
            settingKey="weeklyReportEnabled"
          />

          <NotificationRowWithSms
            icon={Calendar}
            title="Upcoming Bookings Digest"
            subtitle="Evening heads-up when you have bookings the next day"
            emailKey="upcomingBookingsEnabled"
            smsKey="upcomingBookingsSmsEnabled"
          />
        </div>

        {/* ── AoC — Assignment on Confirmation ─────────────────────────────── */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-blue-600" />
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Assignments</h2>
          </div>

          <div className="bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.04)] overflow-hidden">
            <div className="p-4 flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Users className="w-4 h-4 text-blue-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900">Self-assignment for team members</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Unassigned confirmed bookings appear in the <strong>Assignments</strong> tab. Eligible team members claim their own slot — first to tap wins. You always have final say via Edit Booking.
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {[
                      'Role-matched — only eligible team see each trip',
                      '30-min claim window',
                      'Multi-role: Captain + Crew claim independently',
                    ].map(t => (
                      <span key={t} className="text-[11px] text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full border border-gray-100">{t}</span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex-shrink-0 mt-0.5">
                {aocSaving ? (
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                ) : (
                  <ServiceToggle
                    checked={aocEnabled}
                    onChange={handleAocToggle}
                    data-testid="aoc-toggle"
                  />
                )}
              </div>
            </div>

            {aocEnabled && (
              <div className="px-4 pb-4 border-t border-gray-50">
                <p className="text-xs text-gray-400 mt-3">
                  Team members receive a daily SMS digest when there are unassigned trips — one text per day, not per booking. They text <code className="bg-gray-100 px-1 rounded">STOP ASSIGN</code> to opt out.
                </p>
              </div>
            )}
          </div>
        </div>

      </div>
    </TabPageLayout>
  );
};

export default NotificationsTab;
